'use client';
import { create } from 'zustand';
import { ICourseData } from '../types/course-data';
import { CourseType } from '../types/course';
import { LearningPath } from '../types/learning-paths';
import { Lessons } from '../types/lessons';
import { SeenContents } from '../types/seen-contents';
import { UpComingLives } from '../types/up-coming-lives';
import { TrendingSeries } from '../types/trending-series';
import { ContentsInProgress } from '../types/contents-in-progress';
import { CustomerSubscription } from '../types/customer';

type DataType<T> = {
  status: {
    isLoading: boolean;
    isError: boolean;
  };
  data: T | null;
  error: Error | null;
};

type DashboardModal = {
  data: ICourseData | null;
  type: CourseType;
};

type FavouriteDashboard = {
  isLoading: boolean;
  isFavourite: boolean;
  documentId: string | null;
};

type DashboardState = {
  dashboardModal: DashboardModal | null;
  favouriteLearningPath: DataType<LearningPath>;
  favouriteLessons: DataType<Lessons>;
  seenContents: DataType<SeenContents>;
  upComingLives: DataType<UpComingLives>;
  trendingSeries: DataType<TrendingSeries>;
  contentsInProgress: DataType<ContentsInProgress>;
  customerSubscription: DataType<CustomerSubscription>;
  dashboardFavourite: FavouriteDashboard | null;
  setFavouriteDashboard: (data: Partial<FavouriteDashboard>) => void;
  setCustomerSubscription: (data: Partial<DashboardState['customerSubscription']>) => void;
  setContentsInProgress: (data: Partial<DashboardState['contentsInProgress']>) => void;
  setTrendingSeries: (data: Partial<DashboardState['trendingSeries']>) => void;
  setFavouriteLearningPath: (data: Partial<DashboardState['favouriteLearningPath']>) => void;
  setUpComingLives: (data: Partial<DashboardState['upComingLives']>) => void;
  setFavouriteLessons: (data: Partial<DashboardState['favouriteLessons']>) => void;
  setSeenContents: (data: Partial<DashboardState['seenContents']>) => void;
  setDashboardModal: (data: DashboardState['dashboardModal'] | null) => void;
  resetStore: () => void;
};

const initialState: Omit<
  DashboardState,
  | 'setDashboardModal'
  | 'setFavouriteLessons'
  | 'setSeenContents'
  | 'setFavouriteLearningPath'
  | 'setCustomerSubscription'
  | 'setFavouriteDashboard'
  | 'setContentsInProgress'
  | 'setTrendingSeries'
  | 'setUpComingLives'
  | 'resetStore'
> = {
  dashboardFavourite: null,
  dashboardModal: null,
  favouriteLearningPath: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  customerSubscription: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  upComingLives: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  favouriteLessons: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  seenContents: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  contentsInProgress: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  trendingSeries: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
};

export const useDashboardStore = create<DashboardState>((set) => ({
  ...initialState,
  setCustomerSubscription: (data) =>
    set((state) => ({
      customerSubscription: {
        ...state.customerSubscription,
        ...data,
      },
    })),
  setDashboardModal: (data) =>
    set(() => ({
      dashboardModal: data,
    })),
  setFavouriteLearningPath: (data) =>
    set((state) => {
      const existingTitles = state.favouriteLearningPath.data?.data?.map((el) => el?.title) || [];
      return {
        favouriteLearningPath: {
          ...state.favouriteLearningPath,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.favouriteLearningPath.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.favouriteLearningPath.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.favouriteLearningPath.data,
        },
      };
    }),
  setContentsInProgress: (data) =>
    set((state) => {
      const existingTitles = state.contentsInProgress.data?.data?.map((el) => el?.title) || [];
      return {
        contentsInProgress: {
          ...state.contentsInProgress,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.contentsInProgress.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.contentsInProgress.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.contentsInProgress.data,
        },
      };
    }),
  setUpComingLives: (data) =>
    set((state) => {
      const existingTitles = state.upComingLives.data?.data?.map((el) => el?.title) || [];
      return {
        upComingLives: {
          ...state.upComingLives,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.upComingLives.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.upComingLives.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.upComingLives.data,
        },
      };
    }),
  setTrendingSeries: (data) =>
    set((state) => {
      const existingTitles = state.trendingSeries.data?.data?.map((el) => el?.title) || [];
      return {
        trendingSeries: {
          ...state.trendingSeries,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.trendingSeries.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.trendingSeries.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.trendingSeries.data,
        },
      };
    }),
  setSeenContents: (data) =>
    set((state) => {
      const existingTitles = state.seenContents.data?.data?.map((el) => el?.title) || [];
      return {
        seenContents: {
          ...state.seenContents,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.seenContents.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.seenContents.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.seenContents.data,
        },
      };
    }),
  setFavouriteLessons: (data) =>
    set((state) => {
      const existingTitles = state.favouriteLessons.data?.data?.map((el) => el?.title) || [];
      return {
        favouriteLessons: {
          ...state.favouriteLessons,
          ...data,
          data: data?.data
            ? {
                pagination: {
                  ...state.favouriteLessons.data?.pagination,
                  ...data.data.pagination,
                },
                data: [
                  ...(state.favouriteLessons.data?.data || []),
                  ...(data.data.data?.filter((el) => !existingTitles?.includes(el?.title)) || []),
                ],
              }
            : state.favouriteLessons.data,
        },
      };
    }),
  setFavouriteDashboard: (data) =>
    set((state) => ({
      dashboardFavourite: state.dashboardFavourite
        ? { ...state.dashboardFavourite, ...data }
        : { isLoading: false, isFavourite: false, documentId: null, ...data },
    })),
  resetStore: () =>
    set(() => ({
      ...initialState,
    })),
}));
